import { useEffect, useState, createContext, useContext, useCallback, useRef } from 'react';
import api from '@/lib/api';

/**
 * PatientLocationChips
 *
 * Muestra chips iniciales con el color del centro donde fue vista la
 * paciente. Ej.: "S.M.P.  [I] [S]" indica visitas en IMED y La Salud.
 * Al hover, tooltip con nombre completo + última visita.
 *
 * Optimización: usa un contexto compartido con cache + batch para pedir
 * los datos de MUCHOS patient_ids en una sola llamada al backend, no 1600.
 */

const LocationChipsContext = createContext(null);

export function LocationChipsProvider({ children }) {
  const cacheRef = useRef(new Map()); // patient_id → chips[]
  const pendingIdsRef = useRef(new Set());
  const timerRef = useRef(null);
  const subscribersRef = useRef(new Map()); // patient_id → Set(callback)
  const inflightRef = useRef(false);

  const flushBatch = useCallback(async () => {
    if (inflightRef.current) return;
    const ids = Array.from(pendingIdsRef.current);
    if (ids.length === 0) return;
    pendingIdsRef.current = new Set();
    inflightRef.current = true;
    try {
      const { data } = await api.post('/patients/location-chips', { patient_ids: ids });
      const map = data?.chips || {};
      ids.forEach(id => {
        const chips = map[id] || [];
        cacheRef.current.set(id, chips);
        // notify subscribers for this id
        const subs = subscribersRef.current.get(id);
        if (subs) subs.forEach(cb => cb(chips));
      });
    } catch { /* silent */ }
    finally { inflightRef.current = false; }
    // If new ids arrived during the request, flush again
    if (pendingIdsRef.current.size > 0) {
      setTimeout(flushBatch, 50);
    }
  }, []);

  const requestChips = useCallback((patientId, cb) => {
    if (!patientId) return () => {};
    // Suscribir
    if (!subscribersRef.current.has(patientId)) {
      subscribersRef.current.set(patientId, new Set());
    }
    subscribersRef.current.get(patientId).add(cb);
    // Si ya está en cache, resolver inmediato
    if (cacheRef.current.has(patientId)) {
      cb(cacheRef.current.get(patientId));
      return () => subscribersRef.current.get(patientId)?.delete(cb);
    }
    // Si no, encolar para batch
    pendingIdsRef.current.add(patientId);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushBatch, 80); // debounce corto
    return () => subscribersRef.current.get(patientId)?.delete(cb);
  }, [flushBatch]);

  return (
    <LocationChipsContext.Provider value={{ requestChips }}>
      {children}
    </LocationChipsContext.Provider>
  );
}

export function PatientLocationChips({ patientId, size = 'sm' }) {
  const ctx = useContext(LocationChipsContext);
  const [chips, setChips] = useState(null);

  useEffect(() => {
    if (!ctx || !patientId) return;
    const unsub = ctx.requestChips(patientId, setChips);
    return unsub;
  }, [ctx, patientId]);

  if (!chips || chips.length === 0) return null;

  const dim = size === 'xs' ? 12 : (size === 'md' ? 18 : 14);
  const fontSize = size === 'xs' ? 8 : (size === 'md' ? 11 : 9);

  return (
    <span className="inline-flex gap-0.5 align-middle" data-testid={`patient-loc-chips-${patientId}`}>
      {chips.map(c => (
        <span
          key={c.id}
          title={`${c.name} · última visita ${c.last_visit || 'sin fecha'} · ${c.visits} visita${c.visits !== 1 ? 's' : ''}`}
          className="inline-flex items-center justify-center rounded-sm text-white font-bold select-none flex-shrink-0"
          style={{
            width: `${dim}px`,
            height: `${dim}px`,
            fontSize: `${fontSize}px`,
            backgroundColor: c.color,
            lineHeight: 1,
          }}
        >
          {c.initial}
        </span>
      ))}
    </span>
  );
}
